import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { updateCache } from "@/services/backend/_lib/update-cache";
import { backendApi } from "../../api";
import {
  OutboxAuthorizationReferenceFilters,
  OutboxBaseFilters,
  OutboxCustomerReferenceFilters,
  OutboxDocumentReferenceFilters,
} from "../types/outbox";
import {
  OutboxGesecInvoice,
  OutboxGesecInvoiceList,
} from "../types/outbox-gesec-invoice";

export const outboxGesecInvoiceApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    businessCentralOutboxGesecInvoicePut: build.mutation<
      OutboxGesecInvoice,
      OutboxGesecInvoicePutRequest
    >({
      query: (request) => ({
        url: `/business-central/outbox/gesec-invoice`,
        method: "PUT",
        body: request,
      }),
      // The outbox page patches the cached invoice optimistically and renders
      // from it, so a successful save must NOT invalidate that entry: the
      // refetch would land mid-typing and overwrite the input. On error the
      // detail tag is invalidated on purpose — the refetch is what resets the
      // optimistic value back to the server state.
      invalidatesTags: (_result, error, request) =>
        error
          ? [{ type: "OutboxGesecInvoice", id: request.outboxId }]
          : [{ type: "OutboxGesecInvoice", id: "LIST" }],
    }),
    businessCentralOutboxGesecInvoicesShow: build.query<
      OutboxGesecInvoice,
      OutboxGesecInvoiceShowRequest
    >({
      query: (request) => ({
        url: `/business-central/outbox/gesec-invoice`,
        method: "GET",
        params: request,
      }),
      providesTags: (_result, _error, request) => [
        { type: "OutboxGesecInvoice", id: request.outboxId },
      ],
    }),
    businessCentralOutboxGesecInvoicesList: build.query<
      OutboxGesecInvoiceList,
      OutboxGesecInvoiceListRequest
    >({
      query: (request) => ({
        url: `/business-central/outbox/gesec-invoice/list`,
        method: "GET",
        params: { request: btoa(JSON.stringify(request)) },
      }),
      // Not `providesList`: that helper keys items by `id`, which here is the
      // Business Central invoice id, not the outbox id the entries are cached by.
      providesTags: (result) => [
        { type: "OutboxGesecInvoice", id: "LIST" },
        ...(result?.items ?? []).map((item) => ({
          type: "OutboxGesecInvoice" as const,
          id: item.outboxId,
        })),
      ],
    }),
    businessCentralOutboxGesecInvoiceSend: build.mutation<
      OutboxGesecInvoice,
      OutboxGesecInvoiceSendRequest
    >({
      query: (request) => ({
        url: `/business-central/outbox/gesec-invoice/send`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "OutboxGesecInvoice", id: request.outboxId },
        { type: "OutboxGesecInvoice", id: "LIST" },
      ],
    }),
    businessCentralOutboxGesecInvoiceDelete: build.mutation<
      OutboxGesecInvoice,
      OutboxGesecInvoiceDeleteRequest
    >({
      query: (request) => ({
        url: `/business-central/outbox/gesec-invoice`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "OutboxGesecInvoice", id: request.outboxId },
        { type: "OutboxGesecInvoice", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useBusinessCentralOutboxGesecInvoicePutMutation,
  useBusinessCentralOutboxGesecInvoicesShowQuery,
  useBusinessCentralOutboxGesecInvoicesListQuery,
  useBusinessCentralOutboxGesecInvoiceSendMutation,
  useBusinessCentralOutboxGesecInvoiceDeleteMutation,
} = outboxGesecInvoiceApi;

export type OutboxGesecInvoicePutRequest = OutboxGesecInvoice;

export type OutboxGesecInvoiceShowRequest = {
  outboxId: string;
};

export type OutboxGesecInvoiceListRequest = OutboxBaseFilters &
  OutboxAuthorizationReferenceFilters &
  OutboxCustomerReferenceFilters &
  OutboxDocumentReferenceFilters & {
    orderBy?: OrdrBy<OutboxGesecInvoiceOrderByFields>[];
    limit?: number;
    offset?: number;
  };

export type OutboxGesecInvoiceOrderByFields =
  "outboxStatus" | "outboxCreatedAt" | "outboxUpdatedAt";

export type OutboxGesecInvoiceSendRequest = {
  outboxId: string;
};

export type OutboxGesecInvoiceDeleteRequest = {
  outboxId: string;
};

/**
 * Patches the cached invoice for an optimistic update. The show query is keyed
 * by `outboxId`, not by the default `id` — which here is the Business Central
 * invoice id. Defined here, at module scope, so it keeps one identity for the
 * whole app — see {@link updateCache}.
 */
export const updateOutboxGesecInvoiceCache = updateCache(
  outboxGesecInvoiceApi,
  "businessCentralOutboxGesecInvoicesShow",
  (invoice) => ({ outboxId: invoice.outboxId }),
);
